import React, { useState, useEffect } from 'react';
import { SearchIcon, CloseIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { useSearchStrategicPillars, useStrategicPillarHighlights, useUpdateStrategicPillarHighlights, useStrategicPillars } from '@/lib/api/hooks/strategic-pillar';
import { showToast } from '@/lib/toast';
import { StrategicPillar } from '@/lib/api/types';
import { WebsitePageSection } from '@/types';
import { X } from 'lucide-react';

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

interface InlineStrategicPillarEditorProps {
    data: WebsitePageSection;
    onSave: (data: Partial<WebsitePageSection>) => void;
    onCancel: () => void;
}

const InlineStrategicPillarEditor: React.FC<InlineStrategicPillarEditorProps> = ({
    onCancel,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedPillars, setSelectedPillars] = useState<StrategicPillar[]>([]);

    const { data: allPillarsData, isLoading: isAllLoading } = useStrategicPillars(
        { page: 1, size: 20 },
        { status: 'PUBLISH' }
    );

    const { data: searchResultsData, isLoading: isSearchLoading } = useSearchStrategicPillars(
        { page: 1, size: 20 },
        searchQuery,
        !!searchQuery
    );

    const allStrategicPillars = searchQuery
        ? (searchResultsData?.data?.data || [])
        : (allPillarsData?.data?.data || []);

    // Filter out already selected pillars
    const strategicPillars = allStrategicPillars.filter((sp: StrategicPillar) => !selectedIds.includes(sp.strategicPillarId));

    const isLoading = searchQuery ? isSearchLoading : isAllLoading;

    const { data: highlightsData } = useStrategicPillarHighlights();
    const updateHighlightsMutation = useUpdateStrategicPillarHighlights();

    useEffect(() => {
        if (highlightsData?.data) {
            const currentIds = highlightsData.data.map((sp: StrategicPillar) => sp.strategicPillarId);
            setSelectedIds(currentIds);
            setSelectedPillars(highlightsData.data);
        }
    }, [highlightsData]);

    const handleToggleSelect = (strategicPillarId: string) => {
        const isAlreadySelected = selectedIds.includes(strategicPillarId);

        if (isAlreadySelected) {
            setSelectedIds(prev => prev.filter(id => id !== strategicPillarId));
            setSelectedPillars(prev => prev.filter(p => p.strategicPillarId !== strategicPillarId));
        } else {
            if (selectedIds.length >= 3) {
                showToast.error("You can only select up to 3 strategic pillars.");
                return;
            }
            const pillarToAdd = allStrategicPillars.find((p: StrategicPillar) => p.strategicPillarId === strategicPillarId);
            if (pillarToAdd) {
                setSelectedIds(prev => [...prev, strategicPillarId]);
                setSelectedPillars(prev => [...prev, pillarToAdd]);
            }
        }
    };

    const handleSave = async () => {
        try {
            await updateHighlightsMutation.mutateAsync(selectedIds);
            showToast.success("Strategic pillars updated successfully!");
            onCancel();
        } catch (error) {
            showToast.error("Failed to update strategic pillars. Please try again.");
        }
    };

    const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-lg text-gray-900">Select Strategic Pillars</h3>
                <button
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700 p-1"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            </div>

            {selectedPillars.length > 0 && (
                <div className="p-4 border-b border-gray-200 bg-wine-50/30">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Currently Selected ({selectedPillars.length}/3)</h4>
                    </div>
                    <div className="space-y-2">
                        {selectedPillars.map((sp) => (
                            <div
                                key={`selected-${sp.strategicPillarId}`}
                                className="flex items-start gap-3 bg-white border border-[#681628] rounded-lg p-3 group"
                            >
                                <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                    <img
                                        src={sp.coverImage || defaultImage}
                                        alt={sp.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            if (e.currentTarget.src !== defaultImage) {
                                                e.currentTarget.src = defaultImage;
                                            }
                                        }}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{sp.title}</h5>
                                    {(sp.datePublished || sp.createdByUsername) && (
                                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                            {sp.datePublished && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(sp.datePublished)}</p>
                                            )}
                                            {sp.createdByUsername && (
                                                <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {sp.createdByUsername}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleToggleSelect(sp.strategicPillarId)}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                    title="Remove from selection"
                                >
                                    <X className="w-4 h-4 text-gray-500 group-hover:text-[#681628]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="p-4 border-b border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search strategic pillars"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-10 pl-4 pr-12 bg-gray-50 text-base font-normal text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#951F39] focus:border-transparent"
                    />
                    {searchQuery ? (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center hover:bg-[#801b31] transition-colors"
                        >
                            <CloseIcon className="w-5 h-5 text-white" />
                        </button>
                    ) : (
                        <div className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center">
                            <SearchIcon className="w-5 h-5 text-white" />
                        </div>
                    )}
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {isLoading ? (
                    <div className="text-center py-8 text-gray-500">Loading strategic pillars...</div>
                ) : strategicPillars.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No more strategic pillars available</div>
                ) : (
                    strategicPillars.map((sp: StrategicPillar) => (
                        <div
                            key={sp.strategicPillarId}
                            onClick={() => handleToggleSelect(sp.strategicPillarId)}
                            className="flex items-start gap-4 p-3 rounded-lg border cursor-pointer transition-all border-gray-200 hover:border-gray-300"
                        >
                            <div className="relative w-24 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                <img
                                    src={sp.coverImage || defaultImage}
                                    alt={sp.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        if (e.currentTarget.src !== defaultImage) {
                                            e.currentTarget.src = defaultImage;
                                        }
                                    }}
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm truncate">{sp.title}</h4>
                                {(sp.datePublished || sp.createdByUsername) && (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                        {sp.datePublished && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(sp.datePublished)}</p>
                                        )}
                                        {sp.createdByUsername && (
                                            <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {sp.createdByUsername}</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex-shrink-0 pt-1">
                                <div className="w-6 h-6 rounded-full border border-gray-300" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={updateHighlightsMutation.isPending || selectedIds.length === 0}
                >
                    {updateHighlightsMutation.isPending ? 'Saving...' : 'Save Selection'}
                </Button>
            </div>
        </div>
    );
};

export default InlineStrategicPillarEditor;
